import { memo, useEffect, useState } from 'react';
import {
  animate,
  motion,
  MotionValue,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from 'framer-motion';

import { Character, FontStroke, Segment } from '../models';

import styles from './CharacterView.module.css';

interface FontSegmentSvgProps {
  strokeIndex: number;
  segmentIndex: number;
  segment: Segment;
  color: string;
  animationProgress: MotionValue;
}

const FontSegmentSvg = memo(
  ({
    strokeIndex,
    segmentIndex,
    segment: { path, start, points, length },
    color,
    animationProgress,
  }: FontSegmentSvgProps) => {
    const clipPathId = `clip-${strokeIndex}-${segmentIndex}`;
    const strokeOpacity = useTransform(() =>
      animationProgress.get() <= start ? 0 : 1,
    );
    const strokeDashoffset = useTransform(
      () => length * 0.9999 * ((1 - animationProgress.get()) / (1 - start)),
    );

    return (
      <>
        <defs>
          <clipPath id={clipPathId}>
            <path d={path} />
          </clipPath>
        </defs>
        <motion.polyline
          points={points.map(({ x, y }) => `${x},${y}`).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth={128}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity={strokeOpacity}
          strokeDashoffset={strokeDashoffset}
          strokeDasharray={`${length} ${length}`}
          clipPath={`url(#${clipPathId})`}
          style={{
            WebkitClipPath: `url(#${clipPathId})`,
            clipPath: `url(#${clipPathId})`,
          }}
        />
      </>
    );
  },
);

interface FontStrokeSvgProps {
  strokeIndex: number;
  stroke: FontStroke;
  color: string;
  animated?: boolean;
  onAnimationComplete?: () => void;
}

const FontStrokeAnimation = memo(
  ({
    strokeIndex,
    stroke,
    color,
    animated,
    onAnimationComplete,
  }: FontStrokeSvgProps) => {
    const motionValue = useMotionValue(0);

    useEffect(() => {
      if (animated) {
        const controls = animate(motionValue, 1, { duration: 0.5 });
        return () => controls.stop();
      } else {
        motionValue.jump(0);
        return undefined;
      }
    }, [animated, motionValue]);

    useMotionValueEvent(motionValue, 'animationComplete', () => {
      onAnimationComplete?.();
    });

    return (
      <>
        {stroke.segments.map((segment, i) => (
          <FontSegmentSvg
            key={i}
            strokeIndex={strokeIndex}
            segmentIndex={i}
            segment={segment}
            color={color}
            animationProgress={motionValue}
          />
        ))}
      </>
    );
  },
);

const FontStrokeSvg = memo(
  ({
    strokeIndex,
    stroke,
    color,
    animated,
    onAnimationComplete,
  }: FontStrokeSvgProps) => {
    if (animated !== undefined) {
      return (
        <FontStrokeAnimation
          strokeIndex={strokeIndex}
          stroke={stroke}
          color={color}
          animated={animated}
          onAnimationComplete={onAnimationComplete}
        />
      );
    }

    return (
      <>
        {stroke.segments.map((segment, i) => (
          <path key={i} d={segment.path} fill={color} />
        ))}
      </>
    );
  },
);

export interface CharacterViewProps {
  character: Character | null;
  size: number;
  animatedTo?: number;
  getColor: (
    index: number,
    component: number,
    isShadow: boolean,
  ) => string | undefined;
  onAnimationComplete?: (index: number) => void;
}

export const CharacterView = memo(
  ({
    character: c,
    size,
    animatedTo = -1,
    getColor,
    onAnimationComplete,
  }: CharacterViewProps) => {
    const [character, setCharacter] = useState<Character | null>(null);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [animatedIndex, setAnimatedIndex] = useState(-1);

    useEffect(() => {
      if (c !== character) {
        setCharacter(c);
        setCurrentIndex(-1);
        setAnimatedIndex(-1);
      } else if (animatedIndex < animatedTo) {
        // start with animatedIndex + 1, bound by animatedTo
        setCurrentIndex((value) =>
          Math.min(Math.max(value, animatedIndex + 1), animatedTo),
        );
      } else {
        // stop animation and back to animatedTo
        setCurrentIndex(Math.max(animatedTo, -1));
        setAnimatedIndex(Math.max(animatedTo, -1));
      }
    }, [c, character, animatedIndex, animatedTo]);

    return (
      <div className={styles.characterContainer}>
        <svg
          viewBox="0 0 1024 1024"
          className={styles.characterSvg}
          style={{ width: size, height: size }}
        >
          {character && (
            <g transform="scale(1, -1) translate(0, -900)">
              {
                // shadow
                character.strokes.map((stroke, i) => {
                  const shadowColor = getColor(i, stroke.component, true);
                  return shadowColor ? (
                    <FontStrokeSvg
                      key={`hint-${i}`}
                      strokeIndex={i}
                      stroke={stroke}
                      color={shadowColor}
                    />
                  ) : null;
                })
              }
              {
                // animation
                character.strokes.map((stroke, i) => {
                  const color = getColor(i, stroke.component, false);
                  return color ? (
                    <FontStrokeSvg
                      key={`animated-${i}`}
                      strokeIndex={i}
                      stroke={stroke}
                      color={color}
                      animated={i <= currentIndex}
                      onAnimationComplete={() => {
                        setAnimatedIndex(i);
                        onAnimationComplete?.(i);
                      }}
                    />
                  ) : null;
                })
              }
            </g>
          )}
        </svg>
      </div>
    );
  },
);
